import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Grid2, Typography } from "@mui/material";
import ScreenContainer from "../../Components/ScreenContainer";
import ContinuousChartVisualization from "../../PagesContent/AdvancedAnalytics/ContinuousChartVisualization";
import BinaryChartVisualization from "../../PagesContent/AdvancedAnalytics/BinaryChartVisualization";
import CustomIcon from "../../Components/CustomIcon";
import ChartCalendar from "../../PagesContent/DigitalTwinV3/ChartCalendar";
import { getDatePickerDate } from "../../Helper/DatePicker/DateConverter";
import EntityFilter from "../../PagesContent/AdvancedAnalytics/Header/EntityFilter";
import RedirectIcon from "../../Components/Icons/RedirectIcon";
import { decryptTheParams, encryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import { useNavigate } from "react-router-dom";
import VitalsChartDateCarousel from "../../PagesContent/DigitalTwinV3/Tabs/BatteryVitals/VitalsChartDateCarousel";
import { getRealTimeInfo } from "../../Api/DigitalTwin";
import Loader from "../../Helper/Loaders/Loader";
import { getPagesMetaData } from "../../Api/Api";
import Events from "../../Analytics/Events";
import CODE from "../../Static/Constants/StatusCodes";
import { ThemeProperties } from "../../Theme/ThemeProperties";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import Cookies from "universal-cookie";
import moment from "moment";
import { normalizeSelectedType } from "../../PagesContent/AdvancedAnalytics/Common/advancedAnalysisNav";

export default function AdvancedAnalysis() {
    const location = useLocation();
    const navigate = useNavigate();

    const token = location.search?.substring(1);

    const defaultStartDate = getDatePickerDate(undefined, 0);
    const defaultEndDate = getDatePickerDate(undefined, 0);

    const [selectedEntity, setSelectedEntity] = useState(null);
    const [chartTypes, setChartTypes] = useState(['continuous', 'binary']);
    const [selectedPrimaryGranularity, setSelectedPrimaryGranularity] = useState(null);
    const [calendarDates, setCalendarDates] = useState({
        startDate: defaultStartDate,
        endDate: defaultEndDate,
    });
    const [selectedDate, setSelectedDate] = useState({});
    const [batteryInfo, setBatteryInfo] = useState({});
    const [routeParams, setRouteParams] = useState({
        events: null,
        entityAffectedType: null,
        entityAffectedId: null,
        startDate: null,
        endDate: null,
        category: null,
        eventsSource: null,
    });

    const handleDateChange = ({ startDate, endDate }) => {
        setCalendarDates({ startDate, endDate })
    }

    const [pagesContent, setPagesContent] = useState({
        batteryID: "",
        partner: "",
        tabValue: 0,
        pageHeader: "",
        mounted: false,
    });
    const cookies = new Cookies();
    const zone = cookies.get("timeZone") || "UTC";
    const todayDate = moment().tz(zone).format("YYYY-MM-DD");

    const handleEntitySelection = (entity) => {
        setSelectedEntity(entity);
        if (!selectedPrimaryGranularity) {
            setSelectedPrimaryGranularity("raw");
        }
    }

    const onPrimaryGranularityChange = (granularity) => {
        if (granularity !== selectedPrimaryGranularity) {
            setSelectedPrimaryGranularity(granularity)
        }
    }

    const getBatteryInfo = async () => {
        if (selectedEntity?.id) {
            const realTimeRes = await getRealTimeInfo(selectedEntity?.id, { embed: 'VEHICLE_INFO' });
            setBatteryInfo(realTimeRes?.data || {});
        }
    }

    async function getPageMeta() {
        const pageName = 'advancedAnalysis'
        const res = await getPagesMetaData(pageName)
        Events("Advanced Analysis page opened for particular battery ID", {
            entityID: selectedEntity?.id
        })

        if (res && res?.responseStatus?.code === CODE.SUCCESS) {
            const pageData = res.response?.response?.metaInfo

            setPagesContent({
                ...pagesContent,
                metaInfo: pageData,
                mounted: true
            })

            setChartTypes(pageData?.chartTypes || ['continuous', 'binary']);
        }
    }

    React.useEffect(() => { getPageMeta(); }, []);

    React.useEffect(() => {
        getBatteryInfo();
    }, [selectedEntity?.id]);

    React.useEffect(() => {
        if (token) {
            const decoded = decryptTheParams(token);
            if (decoded?.deviceID) {
                handleEntitySelection({
                    id: decoded.batteryID,
                    value: decoded?.selectedTypeId || decoded.deviceID,
                    // Legacy links send "vehicle" for the licence plate; the Entity
                    // Filter tab and the ID box key it as "licensePlateNo".
                    type: normalizeSelectedType(decoded?.selectedType),
                });

                // Decode and set route params for auto-loading chart
                const events = decoded?.events ? JSON.parse(decoded.events) : null;
                const entityAffectedType = decoded?.entityAffectedType || 'battery';
                const entityAffectedId = decoded?.entityAffectedId || null;
                const alertMetaID = decoded?.alertMetaID ?? null;
                const startDate = decoded?.startDate || null;
                const endDate = decoded?.endDate || null;
                const category = decoded?.category || null;
                const eventsSource = decoded?.eventsSource || null;

                setRouteParams({
                    events,
                    entityAffectedType,
                    entityAffectedId,
                    alertMetaID,
                    startDate,
                    endDate,
                    category,
                    eventsSource,
                });

                // Auto-apply the date range if provided
                if (startDate && endDate) {
                    setCalendarDates({
                        startDate: startDate,
                        endDate: endDate,
                    });
                    // Set selectedDate to startDate for the chart to use
                    setSelectedDate({
                        key: Date.now(),
                        value: startDate
                    });
                }
            }
        }
    }, [token]);


    React.useEffect(() => {
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = ""; // Required for Chrome
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    if (!pagesContent.mounted) {
        return (
            <Box sx={{ height: "100vh", bgcolor: ThemeProperties.backgroundPurpleDarker }}>
                <Loader />
            </Box>
        )
    }

    return (
        <ScreenContainer pagesContent={pagesContent}>
            <Grid2 sx={{ position: 'relative' }}>
                <Grid2 container gap={1} justifyContent={'space-between'} alignItems={'center'} sx={{ py: 2.5, px: 3, bgcolor: '#FFFFFF', borderBottom: '1px solid #E6E6E6' }}>
                    <Grid2 container gap={1} alignItems={'center'}>
                        <CustomIcon name='advanced-analysis-settings' />
                        <Typography variant="fw500s20c000000" sx={{ lineHeight: '28px' }}>{pagesContent?.metaInfo?.pageHeader || "Advanced Analysis"}</Typography>
                    </Grid2>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {selectedEntity?.value && (
                            <PrimaryTooltip title={"Go to Digital Twin"} placement="bottom">
                                <Box
                                    sx={{
                                        mr: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        color: "#6D72F6",
                                        cursor: "pointer",
                                        '&:hover': {
                                            color: "#4D44D8",
                                        }
                                    }}
                                    onClick={() => {
                                        const batteryID = selectedEntity?.id;
                                        if (!batteryID) return;

                                        const temp = {
                                            batteryID: batteryID,
                                            batterySearchedGlobal: "false",
                                            prevLink: "/",
                                            navigateFromTab: true,
                                            tabValue: "overview",
                                        };

                                        encryptTheParams(temp, navigate, true);
                                    }}
                                >
                                    <RedirectIcon sx={{ fontSize: 15 }} />
                                </Box>
                            </PrimaryTooltip>
                        )}
                        <Box sx={{ mr: 1 }}>
                            <EntityFilter handleEntitySelection={handleEntitySelection} defaultEntity={selectedEntity} />
                        </Box>
                        <Box sx={{ mr: 1 }}>
                            <ChartCalendar
                                startDate={calendarDates.startDate}
                                endDate={calendarDates.endDate}
                                onDateChange={handleDateChange}
                                isDisabled={!selectedEntity?.value}
                                dataGapDates={batteryInfo?.dataGaps || []}
                                disableBeforeDate={batteryInfo?.addedOn || ''}
                                disableAfterDate={todayDate}
                            />
                        </Box>
                        {/* <Box sx={{ p: 0.5, borderRadius: '6px', backgroundColor: '#F9F9F9'}}>
                            {primaryGranularities.map(granularity => (
                                <PrimaryGranularityButton
                                    key={granularity}
                                    disabled={!selectedEntity?.value || shouldDisableGranularity(granularity, calendarDates.startDate, calendarDates.endDate)}
                                    onClick={() => onPrimaryGranularityChange(granularity)}
                                    active={selectedPrimaryGranularity === granularity}>
                                    <Typography textTransform="capitalize">{granularity}</Typography>
                                </PrimaryGranularityButton>
                            ))}
                        </Box> */}
                    </Box>
                </Grid2>
                <Box sx={{ p: 2, mb: 6 }}>
                    {chartTypes.includes('continuous') && (
                        <Grid2 container flexDirection={'column'} mb={2.5} pb={2.5}>
                            <Typography variant="fw600s16c000000" sx={{ lineHeight: '20px', mb: 1.5 }}>Continuous Signals</Typography>
                            <ContinuousChartVisualization
                                key={selectedEntity?.id}
                                batteryID={selectedEntity?.id}
                                selectedDate={selectedDate}
                                selectedPrimaryGranularity={selectedPrimaryGranularity}
                                routeParams={routeParams}
                                batteryInfo={batteryInfo}
                                selectedEntity={selectedEntity}
                            />
                        </Grid2>
                    )}
                    {chartTypes.includes('binary') && (
                        <Grid2 container flexDirection={'column'} mb={2.5}>
                            <Typography variant="fw600s16c000000" sx={{ lineHeight: '20px', mb: 1.5 }}>Categorical Signals</Typography>
                            <BinaryChartVisualization
                                batteryID={selectedEntity?.id}
                                selectedDate={selectedDate}
                                selectedPrimaryGranularity={selectedPrimaryGranularity}
                                batteryInfo={batteryInfo}
                                selectedEntity={selectedEntity}
                            />
                        </Grid2>
                    )}
                    <Box sx={{
                        display: 'flex',
                        backgroundColor: '#fff',
                        alignItems: 'center',
                        position: 'fixed',
                        bottom: '0px',
                        width: '100%',
                        zIndex: 999,
                    }}>
                        <Box sx={{ width: 'calc(100% - 175px)', px: '60px' }}>
                            <VitalsChartDateCarousel
                                primaryGranularity={selectedPrimaryGranularity}
                                currentDates={calendarDates}
                                onChangeDate={(date) => {
                                    setSelectedDate({ key: (selectedDate?.key ?? 0) + 1, value: date })
                                }}
                                dataGapDates={[]}
                            />
                        </Box>
                    </Box>
                </Box>
            </Grid2>
        </ScreenContainer >
    );
}
